import { Modal } from "@bluframe/blublocks"
import Button from "components/buttons/Button"
import Pagination from "components/Pagination"
import Typography from "components/Typography"
import { useEffect, useState } from "react"

import type { ComponentProps } from "."
import ProspectQueueDetail from "./ProspectQueueDetail"
import ProspectQueueFilters from "./ProspectQueueFilters"
import ProspectQueueList from "./ProspectQueueList"
import { Content, ErrorBanner, Header, Title, Wrapper } from "./styled"

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)"

const getIsDesktop = () =>
  typeof window !== "undefined"
    ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches
    : false

const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(getIsDesktop)

  useEffect(() => {
    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY)

    const onMediaQueryListChange = () => {
      setIsDesktop(mediaQueryList.matches)
    }

    mediaQueryList.addEventListener("change", onMediaQueryListChange)
    return () =>
      mediaQueryList.removeEventListener("change", onMediaQueryListChange)
  }, [])

  return isDesktop
}

const ProspectQueue = ({
  draftIsIgnored,
  draftManualPriority,
  draftNotes,
  errorMessage,
  filters,
  handles,
  hasActiveFilters,
  isLoading,
  isOverrideSaveDisabled,
  onClearFilters,
  onCloseDetail,
  onDraftIsIgnoredChange,
  onDraftManualPriorityChange,
  onDraftNotesChange,
  onFiltersChange,
  onJustInteracted,
  onRetry,
  onSaveOverrides,
  onSelectHandleClick,
  pagination,
  panelRef,
  selectedHandle
}: ComponentProps) => {
  const isDesktop = useIsDesktop()

  const detailProps = {
    draftIsIgnored,
    draftManualPriority,
    draftNotes,
    isOverrideSaveDisabled,
    onCloseDetail,
    onDraftIsIgnoredChange,
    onDraftManualPriorityChange,
    onDraftNotesChange,
    onJustInteracted,
    onSaveOverrides,
    panelRef,
    selectedHandle
  }

  return (
    <Wrapper>
      <Header>
        <Title>
          <Typography.Paragraph>{"Socials"}</Typography.Paragraph>

          <Typography.H4>{"Prospect Queue"}</Typography.H4>
        </Title>
      </Header>

      {errorMessage ? (
        <ErrorBanner role="alert">
          <Typography.Paragraph>{errorMessage}</Typography.Paragraph>

          {onRetry ? <Button label="Retry" onClick={onRetry} /> : null}
        </ErrorBanner>
      ) : null}

      <ProspectQueueFilters
        filters={filters}
        onClearFilters={onClearFilters}
        onFiltersChange={onFiltersChange}
      />

      <Content>
        <ProspectQueueList
          handles={handles}
          hasActiveFilters={hasActiveFilters}
          isLoading={isLoading}
          onSelectHandleClick={onSelectHandleClick}
          selectedHandle={selectedHandle}
        />

        {isDesktop ? <ProspectQueueDetail {...detailProps} /> : null}
      </Content>

      <Modal
        isOpen={Boolean(selectedHandle) && !isDesktop}
        name="Prospect details"
        onClose={onCloseDetail}
      >
        <ProspectQueueDetail {...detailProps} />
      </Modal>

      <Pagination
        hasNextData={pagination.hasNextData}
        hasPrevData={Boolean(pagination.hasPrevData)}
        link={pagination.link}
        next={pagination.cursor}
        page={pagination.page}
        prev={pagination.prevCursor}
        totalPages={pagination.totalPages}
      />
    </Wrapper>
  )
}

export default ProspectQueue
